import { library } from '@fortawesome/fontawesome-svg-core';
import { faBreadSlice, faClock, faCreditCard, faHandPaper, faHandsWash, faStreetView, faTruck } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
// import * as googleAnalytics from 'workbox-google-analytics';
import { Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import BottomHero from './components/BottomHero/BottomHero';
import CompanyData from './components/CompanyData/CompanyData';
import DocumentHead from './components/DocumentHead/DocumentHead';
import Footer from './components/Footer/Footer';
import HtmlPopup from './components/HtmlPopup/HtmlPopup';
import ImagePopup from './components/ImagePopup/ImagePopup';
import Modal from './components/Modal/Modal';
import OrderForm from './components/OrderForm/OrderForm';
import OrderSummary from './components/OrderSummary/OrderSummary';
import OtpRedirect from './components/OtpRedirect/OtpRedirect';
import ProductList from './components/ProductList/ProductList';
import SimplePayStatus from './components/SimplePayStatus/SimplePayStatus';
import constants from './constants/constants';
import { AppContext } from './context/appContext';
import { PriceContext } from './context/priceContext';
import { CompanyContext } from './context/companyContext';
import services from './services/services';
import utils from './utils/utils';


library.add(faCreditCard, faHandPaper, faTruck, faClock, faBreadSlice, faStreetView, faHandsWash);

function App() {
  const [modalData, setModalData] = useState(constants.modalDataInit);
  const [orderList, setOrderList] = useState({});
  const [orderData, setOrderData] = useState(constants.orderDataInit);
  const [delivery, setDelivery] = useState(false);
  const [paymentType, setPaymentType] = useState('');
  const [errors, setErrors] = useState({});
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [addressData, setAddressData] = useState(constants.addressDataInit);
  const [acceptSimple, setAcceptSimple] = useState(false);

  // 2020-07-07 pjung ERINT-774 Számlázási cím adatai
  const [needsInvoice, setNeedsInvoice] = useState(false);
  const [toCompany, setToCompany] = useState(false);
  const [invName, setInvName] = useState('');
  const [tax, setTax] = useState('');
  const [invAddressData, setInvAddressData] = useState(constants.addressDataInit);

  const [settings, setSettings] = useContext(AppContext);
  const [priceData, setPriceData] = useContext(PriceContext);
  const [messages, setMessages] = useState([]);

  const bottomRef = useRef(null);
  const webSocket = useRef(null);

  const restoreState = () => {
    const prevState = JSON.parse(sessionStorage.getItem('prevState'));
    setOrderList(prevState.orderList);
    setOrderData(prevState.orderData);
    setDelivery(prevState.delivery);
    setPaymentType(prevState.paymentType);
    setAcceptTerms(prevState.acceptTerms);
    setAddressData(prevState.addressData);
    setPriceData(prevState.priceData);
    // 2020-08-14 ERINT-816 Számlázási adatok visszaállítása...
    setNeedsInvoice(prevState.needsInvoice);
    setToCompany(prevState.toCompany);
    setInvName(prevState.invName);
    setTax(prevState.tax);
    setInvAddressData(prevState.invAddressData);

    sessionStorage.removeItem('prevState');
  };

  useEffect(() => {
    const getSettings = async () => {
      // eslint-disable-next-line no-console
      console.log(process.env.REACT_APP_VERSION);
      const response = await axios.get('/react-settings/settings.json');

      if (response.status === 200) {
        const { analytics, fontLink, fontFamily, ...rest } = response.data;
        ReactGA.initialize(analytics);
        ReactGA.pageview(window.location.pathname);

        // googleAnalytics.initialize();
        // gtag('config', analytics);

        setSettings({
          ...settings,
          fontLink: fontLink || constants.fontStyle.fontLink,
          fontFamily: fontFamily || constants.fontStyle.fontFamily,
          ...rest,
        });
      }
    };

    getSettings();

    if (sessionStorage.getItem('prevState')) {
      setTimeout(() => {
        restoreState();
      }, 1500);
    }

    if (sessionStorage.getItem('otpData')) {
      const otpData = JSON.parse(sessionStorage.getItem('otpData'));
      console.log('otpData', otpData);
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsURI = `${wsProtocol}://${window.location.host}${window.location.pathname}ws/${otpData.t}`;
      webSocket.current = new WebSocket(wsURI);

      webSocket.current.onmessage = (message) => {
        console.log('WS message', message);
        const data = JSON.parse(message.data);
        setMessages(data);
      };

      sessionStorage.removeItem('otpData');

      setTimeout(() => {
        window.scrollTo({
          left: 0,
          top: bottomRef.current.offsetTop + 1000,
          behavior: 'smooth',
        });
      }, 1000);

      setTimeout(() => {
        openModal(closeModal, <SimplePayStatus status={otpData.e} id={otpData.t} />, false, otpData.e);
      }, 1001);
    }

    return () => webSocket?.current.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messages.status === 'FINISHED') {
      const data = JSON.parse(sessionStorage.getItem('orderData'));
      openModal(
        null,
        <OrderSummary closeModal={closeModal} {...data} transactionCode={messages.transactionCode} resetState={resetState} />,
        false,
        'default',
        true,
      );
    } else if (messages.status && messages.status !== 'FINISHED') {
      openModal(closeModal, <SimplePayStatus status={messages.status} id={messages.transactionCode} />, true, 'default');
    }
  }, [messages]);

  useEffect(() => {
    window.addEventListener(
      'scroll',
      utils.debounce(() => {
        document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
      }, 200),
    );
  }, []);

  const openModal = (callBack, content, closable, type, fullHeight) => {
    const scrollY = document.documentElement.style.getPropertyValue('--scroll-y');
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}`;
    setModalData({
      open: true,
      callBack,
      closable,
      type,
      content,
      fullHeight,
    });
  };

  const closeModal = () => {
    const { body } = document;
    const scrollY = body.style.top;
    body.style.position = '';
    body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    setModalData(constants.modalDataInit);
  };

  const addOrder = (id, value) => {
    const orderListCopy = { ...orderList };
    setErrors({});

    if (!value) {
      delete orderListCopy[id];
      setOrderList(orderListCopy);
      return;
    }

    setOrderList({
      ...orderListCopy,
      [id]: `${value}`,
    });
  };

  const addOrderData = (event) => {
    const { name, value } = event.target;
    setErrors({});
    if (name === 'phone' && value.length < 3) {
      setOrderData({
        ...orderData,
        phone: '+36',
      });
      return;
    }

    if (name === 'location') {
      setOrderData({
        ...constants.orderDataInit,
        location: value,
      });
      return;
    }

    setOrderData({
      ...orderData,
      [name]: value,
    });
  };

  const addAddressData = (event) => {
    const { name, value } = event.target;

    setErrors({});
    setAddressData({
      ...addressData,
      [name]: value,
    });
  };

  const resetAddress = () => {
    setAddressData(constants.addressDataInit);
  };

  const addOrderDate = (event) => {
    const { value } = event.target;
    const dateValue = new Date(value.slice(0, 10));
    const dateFormat = new Date(dateValue.setHours(dateValue.getHours() + 12)).toISOString().slice(0, 10);

    setOrderData({
      ...orderData,
      date: dateFormat,
    });
  };

  const resetState = () => {
    setAddressData(constants.addressDataInit);
    setOrderData(constants.orderDataInit);
    setErrors({});
    setDelivery(false);
    setOrderList({});
    setAcceptTerms(false);
    // 2020-08-19 ERINT-837 Számlázási adatok visszaállítása...
    setNeedsInvoice(false);
    setToCompany(false);
    setInvName('');
    setTax('');
    setInvAddressData(constants.addressDataInit);
  };

  const readAcceptTerms = async () => {
    if (acceptTerms) {
      setAcceptTerms(false);
      return;
    }
    const accept = () => {
      setAcceptTerms(true);
      closeModal();
    };
    const response = await services.getTerms();

    if (response.data.status === 'OK') {
      openModal(accept, <HtmlPopup data={response.data.data} />, true, 'default');
    }
  };

  const handleAcceptTerms = () => {
    setAcceptTerms(!acceptTerms);
  };

  const handleNeedsInvoice = () => {
    const inv = needsInvoice;
    setNeedsInvoice(!inv);
    if (inv) {
      // Ha nem kell számla, kiürítjük a mezőket
      setToCompany(false);
      setInvName('');
      setTax('');
      resetInvAddress();
    }
  };

  const handleCompany = () => {
    const comp = toCompany;
    setToCompany(!comp);
    if (comp) {
      // Ha nem cég, kiürítjük az adószámot
      setTax('');
    }
  };

  const addInvAddressData = (event) => {
    const { name, value } = event.target;

    setErrors({});
    setInvAddressData({
      ...invAddressData,
      [name]: value,
    });
  };

  const resetInvAddress = () => {
    setInvAddressData(constants.addressDataInit);
  };

  const readSimpleTerms = async () => {
    if (acceptSimple) {
      setAcceptSimple(false);
      return;
    }

    const accept = () => {
      setAcceptSimple(true);
      closeModal();
    };

    const companyUrl = window.location.href;
    const paymentGwData = await services.getPaymentGwData();
    const { name, premise } = paymentGwData.data.data;

    const simpleText = `
      <h3>Adattovábbítási nyilatkozat</h3>
      <p>Jelen ÁSZF elfogadásával tudomásul veszem, hogy a(z) <strong>${name} (${premise})</strong>
      adatkezelő által a(z) <strong><a href="${companyUrl} target="_blank">${companyUrl}</a></strong>
      felhasználói adatbázisában tárolt alábbi személyes adataim átadásra kerülnek az OTP Mobil Kft.,
      mint adatfeldolgozó részére. Az adatkezelő által továbbított adatok köre az alábbi:
      név, e-mail cím, telefonszám, számlázási cím adatok és szállítási cím adatok
      <p>Az adatfeldolgozó által végzett adatfeldolgozási tevékenység jellege és célja
      a SimplePay Adatkezelési tájékoztatóban, az alábbi linken tekinthető meg:
      <a href="http://simplepay.hu/vasarlo-aff" target="_blank">http://simplepay.hu/vasarlo-aff</a></p>
    `;

    openModal(accept, <HtmlPopup data={simpleText} />, true, 'default');
  };

  const handleAcceptSimple = () => {
    setAcceptSimple(!acceptSimple);
  };

  const simplePayment = async (data, prev) => {
    const { orderId, email, total } = data;
    const numberPattern = /\d+/g;
    const request = {
      customerEmail: email,
      orderRef: orderId,
      paymentType: 'SIMPLE',
      total: total.match(numberPattern).join(''),
      // 2020-09-22 pjung ERINT-849 PaymentGW SimplePay PSD2-adatok küldése
      customerName: prev.invName || prev.orderData.name,
      billZIP: prev.invAddressData.zip,
      billCity: prev.invAddressData.city,
      billStreet: prev.invAddressData.street + ' ' + prev.invAddressData.streetType + ' ' + prev.invAddressData.house,
    };
    return services.onlinePayment(request);
  };

  const isSimple = () => {
    return Number(paymentType) === Number(3);
  };

  const sendOrder = async (canOrder) => {
    if (orderData.sent) return;
    else setOrderData({ ...orderData, sent: true });
    setErrors({});

    const { street, streetType, ...rest } = addressData;
    const { street: invStreet, streetType: invStreetType, ...invRest } = invAddressData;
    const request = {
      sum: priceData.total,
      ...orderData,
      productList: { ...orderList },
      address: {
        ...rest,
        street: `${street} ${streetType}`,
      },
      // 2020-07-09 pjung ERINT-774 Számlázási adatok
      invoice: needsInvoice,
      company: toCompany,
      invName: invName,
      tax: tax,
      invAddress: {
        ...invRest,
        street: `${invStreet} ${invStreetType}`,
      },
      sent: undefined,
    };

    //console.log('validate step 1', new Date().toISOString());
    const errorList = await utils.validateForm(request, delivery);
    //console.log('validate step 1.5', new Date().toISOString());
    //console.log(errorList);

    //console.log('validate step 2', new Date().toISOString());
    if (!canOrder) {
      errorList.canOrder = 'Mára már nem lehet leadni rendelést!';
    }

    //console.log('validate step 3', new Date().toISOString());
    if (!acceptTerms) {
      errorList.acceptTerms = 'A felhasználási feltételeket nem fogadta el.';
    }

    //console.log('validate step 4', new Date().toISOString());
    if (Number(priceData.minOrder) > Number(priceData.productTotal)) {
      errorList.location = 'A rendelés összege nem éri el a minimális rendelési összeget';
    }

    //console.log('validate step 5', new Date().toISOString());
    if (isSimple() && !acceptSimple) {
      errorList.acceptSimple = 'Az OTP SimplePay Adattovábbítási nyilatkozatot nem fogadta el.';
    }

    //console.log('validate step 6', new Date().toISOString());
    if (errorList !== undefined && !Object.keys(errorList).length) {
      try {
        const response = await services.sendOrder(request);
        console.log('response = ', response);
        if (response.data.status !== 'OK') {
          openModal(
            closeModal,
            <HtmlPopup
              data={
                // eslint-disable-next-line max-len
                '<h3>Hiba!<h3><p>A rendelés megerősítése során hiba lépett fel, kérjük próbálja meg újra pár perccel később.</p>'
              }
            />,
            false,
            'default',
          );
          return;
        }

        if (!isSimple()) {
          openModal(null, <OrderSummary closeModal={closeModal} {...response.data.data} resetState={resetState} />, false, 'default', true);
          return;
        }
        sessionStorage.setItem('orderData', JSON.stringify(response.data.data));

        // Save previous state...
        const prevState = {
          orderList,
          orderData,
          delivery,
          paymentType,
          errors,
          acceptTerms,
          addressData,
          priceData,
          // 2020-08-12 pjung ERINT-774 Számlázási adatok
          needsInvoice,
          toCompany,
          invName,
          tax,
          invAddressData,
        };

        sessionStorage.setItem('prevState', JSON.stringify(prevState));

        const paymentResponse = await simplePayment(response.data.data, prevState);

        console.log(paymentResponse);
        if (paymentResponse.data.status !== 'OK') {
          openModal(
            closeModal,
            <HtmlPopup
              data={
                // eslint-disable-next-line max-len
                '<h3>Hiba!<h3><p>A rendelés megerősítése során hiba lépett fel, kérjük próbálja meg újra pár perccel később.</p>'
              }
            />,
            false,
            'default',
          );
          return;
        }

        const { paymentUrl } = paymentResponse.data;

        window.location.assign(paymentUrl);
      } catch (error) {
        openModal(
          closeModal,
          <HtmlPopup
            data={
              // eslint-disable-next-line max-len
              `<h3>Hiba!<h3><p>A rendelés leadása során hiba lépett fel, kérjük próbálja meg újra pár perccel később.</p><p>Hibakód: ${error.message}</p>`
            }
          />,
          false,
          'default',
        );
        console.error(error);
      }
    } else setOrderData({ ...orderData, sent: false });
    setErrors(errorList === undefined ? {} : errorList);
  };

  const showImage = (imageUrl) => {
    // TODO
    openModal(closeModal, <ImagePopup imageUrl={imageUrl} />, false, 'image', true);
  };

  return (
    <>
      <DocumentHead />
      <Modal
        open={modalData.open}
        closeModal={modalData.callBack}
        title={modalData.title}
        content={modalData.content}
        closable={modalData.closable}
        type={modalData.type}
        fullHeight={modalData.fullHeight}
        quitModal={closeModal}
      />
      <Root fontFamily={settings?.fontFamily} color={settings?.fontColor}>
        <Switch>
          <Route path="/otpredirect">
            <OtpRedirect webSocket={webSocket} />
          </Route>
          <Route path="/">
            <>
              <CompanyData />
              <ProductList addOrder={addOrder} orderList={orderList} showImage={showImage} />
              <OrderHeader bgcolor={settings?.howtoBackground || '#001524'} color={settings?.howtoColor || '#FFFFFF'}>
                <h3>{settings?.howtoTitle || 'Hogyan tudsz érintésmentesen vásárolni?'}</h3>
              </OrderHeader>
              <OrderForm
                addOrderData={addOrderData}
                addOrderDate={addOrderDate}
                orderData={orderData}
                addressData={addressData}
                addAddressData={addAddressData}
                resetAddress={resetAddress}
                sendOrder={sendOrder}
                setDelivery={setDelivery}
                delivery={delivery}
                errors={errors}
                setErrors={setErrors}
                termsChecked={acceptTerms}
                handleAcceptTerms={handleAcceptTerms}
                readAcceptTerms={readAcceptTerms}
                paymentType={paymentType}
                setPaymentType={setPaymentType}
                isSimple={isSimple}
                handleAcceptSimple={handleAcceptSimple}
                readSimpleTerms={readSimpleTerms}
                simpleTermsChecked={acceptSimple}
                needsInvoice={needsInvoice}
                setNeedsInvoice={setNeedsInvoice}
                handleNeedsInvoice={handleNeedsInvoice}
                toCompany={toCompany}
                handleCompany={handleCompany}
                invName={invName}
                setInvName={setInvName}
                tax={tax}
                setTax={setTax}
                invAddressData={invAddressData}
                addInvAddress={addInvAddressData}
                setInvAddress={setInvAddressData}
              />
              <div ref={bottomRef} />
              <BottomHero />
              <Footer />
            </>
          </Route>
        </Switch>
      </Root>
    </>
  );
}

export default App;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${(props) => props.fontFamily};
  color: ${(props) => props.color};
`;

const OrderHeader = styled.div`
  width: 100;
  background-color: ${(props) => props.bgcolor};

  & h3 {
    color: ${(props) => props.color};
    text-align: center;
    text-transform: uppercase;
    font-size: 3em;

    @media (max-width: 800px) {
      font-size: 2.5em;
    }

    @media (max-width: 500px) {
      font-size: 2em;
    }
  }
`;
